import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import useLogoReset from '../../hooks/useLogoReset';
import useClearSelectionOnFilterChange from '../../hooks/useClearSelectionOnFilterChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Plus, Search, RefreshCw, Loader2, MoreHorizontal, Edit2, Trash2,
  MapPin, Calendar, User, AlertTriangle,
  Settings2, GripVertical, Save, X, Check
} from 'lucide-react';
import { AddLocalLoad } from './AddLocalLoad';
import { EditLocalLoad } from './EditLocalLoad';
import { useBulkSelection, useColumnManagement, useInlineEdit, useInAppSearchShortcut } from '../../hooks';
import { StatsCard, StatsGrid, BulkSelectionBar } from './components';

const STATUS_COLORS = {
  'New Load': '#E6E6FA',
  'Dispatched': '#90EE90',
  'In Transit': '#FFB6C1',
  'At Pickup': '#FFDAB9',
  'At Delivery': '#87CEEB',
  'Delivered': '#98FB98',
  'Completed': '#D3D3D3',
  'Cancelled': '#FA8072',
  'On Hold': '#FFD700'
};

const STATUSES = Object.keys(STATUS_COLORS);

// Default column configuration
const DEFAULT_COLUMNS = [
  { id: 'job_number', label: 'Load #', visible: true, width: 80 },
  { id: 'load_type', label: 'Type', visible: true, width: 60 },
  { id: 'status', label: 'Status', visible: true, width: 100 },
  { id: 'pickup_location', label: 'Origin', visible: true, width: 120 },
  { id: 'delivery_location', label: 'Destination', visible: true, width: 120 },
  { id: 'customer_name', label: 'Customer', visible: true, width: 100 },
  { id: 'driver_name', label: 'Driver', visible: true, width: 80 },
  { id: 'pickup_date', label: 'P/U', visible: true, width: 70 },
  { id: 'delivery_date', label: 'Del', visible: false, width: 70 },
  { id: 'rate', label: 'Rate', visible: true, width: 70 },
  { id: 'weight', label: 'Wt', visible: false, width: 60 },
  { id: 'commodity', label: 'Commodity', visible: false, width: 90 },
  { id: 'remarks', label: 'Notes', visible: false, width: 100 },
];

export const LocalDashboard = ({ masterData, customers }) => {
  const { api } = useAuth();
  // Hijack Ctrl/Cmd+F → in-app search (server-backed, full dataset).
  useInAppSearchShortcut('[data-testid="local-search-input"]');
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  // iter 265.3 — Row Selection Persistence preference (shared with other dashboards)
  const [filterClearSelection, setFilterClearSelection] = useState(false);
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [lastEditedLoadId, setLastEditedLoadId] = useState(null);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Use shared bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelection,
    toggleSelectAll,
    isSelected,
    isAllSelected,
    isSomeSelected,
    deselectAll
  } = useBulkSelection({
    items: loads,
    idField: 'id'
  });

  // iter 265.2 — Reset filters + selection when the Drayly logo is clicked.
  useLogoReset(() => {
    setSearchTerm('');
    setStatusFilter('all');
    setDriverFilter('all');
    setCustomerFilter('all');
    try { deselectAll?.(); } catch (_) { /* ignore */ }
  });

  // iter 265.3 — Auto-clear bulk selection when a filter changes (opt-in).
  useClearSelectionOnFilterChange({
    enabled: filterClearSelection,
    deselectAll,
    deps: [statusFilter, searchTerm, driverFilter, customerFilter],
  });

  // Fetch shared preference so the sticky/auto-clear behavior matches the
  // rest of the app.
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get('/user/preferences');
        if (typeof resp.data?.filter_clear_selection === 'boolean') {
          setFilterClearSelection(resp.data.filter_clear_selection);
        }
      } catch (_) { /* ignore */ }
    })();
  }, [api]);

  // Use column management hook
  const {
    columns,
    visibleColumns,
    columnsChanged,
    savingColumns,
    draggedColumn,
    dragOverColumn,
    loadColumnPreferences,
    saveColumnPreferences,
    toggleColumnVisibility,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleResizeStart,
    resetToDefaults
  } = useColumnManagement({
    defaultColumns: DEFAULT_COLUMNS,
    storageKey: 'local_loads',
    api
  });

  // Use inline edit hook
  const {
    editCell,
    editValue,
    setEditValue,
    saving: savingEdit,
    inputRef,
    startEdit,
    cancelEdit,
    saveEdit,
    handleKeyDown,
    isEditing
  } = useInlineEdit({
    onSave: async (loadId, field, value) => {
      await api.put(`/local-loads/${loadId}`, { [field]: value || null });
      setLoads(prev => prev.map(load => 
        load.id === loadId ? { ...load, [field]: value } : load
      ));
      setLastEditedLoadId(loadId);
      toast.success('Updated successfully');
      setTimeout(() => setLastEditedLoadId(null), 3000);
    }
  });

  // Load column preferences on mount
  useEffect(() => {
    loadColumnPreferences();
  }, [loadColumnPreferences]);

  // Render inline edit cell using the hook
  const renderEditableCell = (load, field, displayValue, width = 'w-32') => {
    if (isEditing(load.id, field)) {
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`${width} h-7 text-xs bg-slate-800 border-blue-500`}
            onClick={(e) => e.stopPropagation()}
          />
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); saveEdit(); }} disabled={savingEdit}>
            <Check className="h-3 w-3 text-green-400" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); cancelEdit(); }}>
            <X className="h-3 w-3 text-red-400" />
          </Button>
        </div>
      );
    }
    
    return (
      <span 
        className="cursor-pointer hover:bg-slate-800 px-1 py-0.5 rounded"
        onClick={(e) => { e.stopPropagation(); startEdit(load.id, field, load[field]); }}
        title="Click to edit"
      >
        {displayValue || '-'}
      </span>
    );
  };

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (driverFilter && driverFilter !== 'all') params.driver_id = driverFilter;
      if (customerFilter && customerFilter !== 'all') params.customer_id = customerFilter;
      
      const response = await api.get('/local-loads', { params });
      setLoads(response.data || []);
    } catch (error) {
      console.error('Failed to fetch local loads:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, searchTerm, driverFilter, customerFilter]);

  useEffect(() => {
    fetchLoads();
  }, [fetchLoads]);

  // Handle column header click for sorting
  const handleColumnSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  const clearSort = () => {
    setSortColumn(null);
    setSortDirection('asc');
  };

  // Sorted loads based on column selection
  const sortedLoads = [...loads].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    
    if (aVal === null || aVal === undefined || aVal === '') aVal = sortDirection === 'asc' ? 'zzzzz' : '';
    if (bVal === null || bVal === undefined || bVal === '') bVal = sortDirection === 'asc' ? 'zzzzz' : '';
    
    if (sortColumn.includes('date')) {
      aVal = aVal ? new Date(aVal).getTime() : (sortDirection === 'asc' ? Infinity : 0);
      bVal = bVal ? new Date(bVal).getTime() : (sortDirection === 'asc' ? Infinity : 0);
    }
    
    if (sortColumn === 'rate' || sortColumn === 'weight') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedCount === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedCount} load(s)?`)) return;
    
    try {
      // Delete all selected loads
      await Promise.all(
        Array.from(selectedIds).map(id => api.delete(`/local-loads/${id}`))
      );
      toast.success(`${selectedCount} load(s) deleted`);
      deselectAll();
      fetchLoads();
    } catch (error) {
      toast.error('Failed to delete some loads');
    }
  };

  // Bulk status change handler
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedCount === 0) return;
    
    try {
      await Promise.all(
        Array.from(selectedIds).map(id => 
          api.put(`/local-loads/${id}`, { status: newStatus })
        )
      );
      toast.success(`${selectedCount} load(s) updated to ${newStatus}`);
      deselectAll();
      fetchLoads();
    } catch (error) {
      toast.error('Failed to update some loads');
    }
  };

  const handleDeleteLoad = async (loadId) => {
    if (!confirm('Are you sure you want to delete this load?')) return;
    try {
      await api.delete(`/local-loads/${loadId}`);
      toast.success('Load deleted');
      fetchLoads();
    } catch (error) {
      toast.error('Failed to delete load');
    }
  };

  const handleEditLoad = (load) => {
    setSelectedLoad(load);
    setEditSheetOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MM/dd/yy');
    } catch {
      return '-';
    }
  };

  const getStatusColor = (status) => STATUS_COLORS[status] || '#94a3b8';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search loads..."
              className="pl-9 bg-slate-900 border-slate-800"
              data-testid="local-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-800">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="all" className="text-white">All Statuses</SelectItem>
              {STATUSES.map(s => (
                <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={driverFilter} onValueChange={setDriverFilter}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-800">
              <SelectValue placeholder="All Drivers" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="all" className="text-white">All Drivers</SelectItem>
              {(masterData.drivers || []).map(d => (
                <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-800">
              <SelectValue placeholder="All Customers" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="all" className="text-white">All Customers</SelectItem>
              {(customers || []).map(c => (
                <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {sortColumn && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSort}
              className="text-blue-400 hover:text-white"
              data-testid="clear-sort-btn"
            >
              <X className="h-4 w-4 mr-1" />
              Sort: {sortColumn}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchLoads} className="border-slate-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          
          {/* Column Settings */}
          <DropdownMenu open={showColumnSettings} onOpenChange={setShowColumnSettings}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="border-slate-700">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 w-56 max-h-80 overflow-y-auto">
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-400">Toggle Columns</div>
              <DropdownMenuSeparator className="bg-slate-700" />
              {columns.map(col => (
                <DropdownMenuItem
                  key={col.id}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleColumnVisibility(col.id);
                  }}
                  className="text-white cursor-pointer"
                >
                  <Checkbox
                    checked={col.visible}
                    className="mr-2 border-slate-600"
                  />
                  {col.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem
                onClick={resetToDefaults}
                className="text-blue-400"
              >
                Reset to Defaults
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Save Columns Button */}
          {columnsChanged && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => saveColumnPreferences(columns)}
              disabled={savingColumns}
              className="border-green-600 text-green-400 hover:bg-green-900/30"
            >
              {savingColumns ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Layout
            </Button>
          )}
          
          <Button onClick={() => setAddSheetOpen(true)} className="bg-green-600 hover:bg-green-500" data-testid="add-local-load-btn">
            <Plus className="h-4 w-4 mr-2" />Add Local Load
          </Button>
        </div>
      </div>

      {/* Stats - Using reusable StatsGrid/StatsCard */}
      <StatsGrid columns={4}>
        <StatsCard
          title="Total Loads"
          value={loads.length}
          valueColor="text-white"
        />
        <StatsCard
          title="In Transit"
          value={loads.filter(l => l.status === 'In Transit').length}
          valueColor="text-green-400"
        />
        <StatsCard
          title="Delivered"
          value={loads.filter(l => l.status === 'Delivered').length}
          valueColor="text-blue-400"
        />
        <StatsCard
          title="Total Revenue"
          value={`$${loads.reduce((sum, l) => sum + (l.rate || 0), 0).toLocaleString()}`}
          valueColor="text-amber-400"
        />
      </StatsGrid>

      {/* Bulk Selection Bar - Using reusable component */}
      <BulkSelectionBar
        selectedCount={selectedCount}
        onClearSelection={deselectAll}
        actions={[
          ...STATUSES.map(status => ({
            label: `Set to ${status}`,
            onClick: () => handleBulkStatusChange(status),
            testId: `bulk-set-${status.toLowerCase().replace(/\s+/g, '-')}`
          })),
          {
            label: 'Delete Selected',
            onClick: handleBulkDelete,
            className: 'border-red-600 text-red-300 hover:bg-red-600',
            icon: Trash2,
            testId: 'bulk-delete-btn'
          }
        ]}
      />

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <ScrollArea className="h-[calc(100vh-350px)]">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-900 z-10">
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400 w-10">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className={isSomeSelected && !isAllSelected ? 'data-[state=checked]:bg-blue-500' : ''}
                    data-testid="select-all-local"
                  />
                </TableHead>
                {visibleColumns.map(col => (
                  <TableHead 
                    key={col.id}
                    className={`text-slate-400 cursor-pointer hover:bg-slate-800/50 ${dragOverColumn === col.id ? 'bg-blue-900/30' : ''} ${sortColumn === col.id ? 'bg-slate-800/70 text-blue-400' : ''}`}
                    style={{ width: col.width, minWidth: col.width }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, col.id)}
                    onDragOver={(e) => handleDragOver(e, col.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleColumnSort(col.id)}
                    title={`Click to sort by ${col.label}`}
                  >
                    <div className="flex items-center gap-1 group">
                      <GripVertical className="h-3 w-3 text-slate-600 opacity-0 group-hover:opacity-100 cursor-grab" />
                      <span>{col.label}</span>
                      {sortColumn === col.id && (
                        <span className="ml-1 text-blue-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                      <div 
                        className="w-1 h-4 cursor-col-resize hover:bg-blue-500 ml-auto"
                        onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(e, col.id, col.width); }}
                      />
                    </div>
                  </TableHead>
                ))}
                <TableHead className="text-slate-400 w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow key="loading-row">
                  <TableCell colSpan={visibleColumns.length + 2} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-500" />
                  </TableCell>
                </TableRow>
              ) : loads.length === 0 ? (
                <TableRow key="empty-row">
                  <TableCell colSpan={visibleColumns.length + 2} className="text-center py-8 text-slate-500">
                    No local loads found. Click &quot;Add Local Load&quot; to create one.
                  </TableCell>
                </TableRow>
              ) : (
                sortedLoads.map(load => (
                  <TableRow 
                    key={load.id} 
                    className={`border-slate-800 hover:bg-slate-800/50 cursor-pointer ${isSelected(load.id) ? 'bg-blue-900/20' : ''} ${lastEditedLoadId === load.id ? 'border-l-2 border-l-green-500' : ''}`}
                    onClick={() => handleEditLoad(load)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected(load.id)}
                        onCheckedChange={() => toggleSelection(load.id)}
                        data-testid={`select-local-${load.id}`}
                      />
                    </TableCell>
                    {visibleColumns.map(col => (
                      <TableCell 
                        key={col.id} 
                        className="text-slate-300"
                        style={{ width: col.width, minWidth: col.width }}
                        onClick={(e) => ['status'].includes(col.id) && e.stopPropagation()}
                      >
                        {col.id === 'job_number' && (
                          <div className="flex items-center gap-2 font-mono text-white">
                            {load.is_important && <AlertTriangle className="h-4 w-4 text-red-400" />}
                            {load.job_number?.replace('LOC-', '')}
                          </div>
                        )}
                        {col.id === 'load_type' && (
                          <Badge variant="outline" className="text-xs">{load.load_type}</Badge>
                        )}
                        {col.id === 'status' && (
                          <Select 
                            value={load.status} 
                            onValueChange={async (newStatus) => {
                              try {
                                await api.put(`/local-loads/${load.id}`, { status: newStatus });
                                setLoads(prev => prev.map(l => l.id === load.id ? { ...l, status: newStatus } : l));
                                setLastEditedLoadId(load.id);
                                setTimeout(() => setLastEditedLoadId(null), 3000);
                                toast.success('Status updated');
                              } catch (error) {
                                toast.error('Failed to update status');
                              }
                            }}
                          >
                            <SelectTrigger 
                              className="w-28 h-7 text-xs border-0 p-1"
                              style={{ backgroundColor: getStatusColor(load.status), color: '#1a1a1a' }}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800">
                              {STATUSES.map(s => (
                                <SelectItem key={s} value={s} className="text-white text-xs">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getStatusColor(s) }} />
                                    {s}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {col.id === 'pickup_location' && renderEditableCell(load, 'pickup_city', 
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-green-400" />
                            <span className="truncate max-w-[100px]">{load.pickup_city}{load.pickup_state ? `, ${load.pickup_state}` : ''}</span>
                          </div>, 'w-28'
                        )}
                        {col.id === 'delivery_location' && renderEditableCell(load, 'delivery_city', 
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-blue-400" />
                            <span className="truncate max-w-[100px]">{load.delivery_city}{load.delivery_state ? `, ${load.delivery_state}` : ''}</span>
                          </div>, 'w-28'
                        )}
                        {col.id === 'customer_name' && renderEditableCell(load, 'customer_name', load.customer_name, 'w-28')}
                        {col.id === 'driver_name' && renderEditableCell(load, 'driver_name', 
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-slate-500" />
                            {load.driver_name || '-'}
                          </div>, 'w-24'
                        )}
                        {col.id === 'pickup_date' && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-slate-500" />
                            {formatDate(load.pickup_date)}
                          </div>
                        )}
                        {col.id === 'delivery_date' && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-slate-500" />
                            {formatDate(load.delivery_date)}
                          </div>
                        )}
                        {col.id === 'rate' && (
                          <span className="text-green-400 font-medium">
                            {renderEditableCell(load, 'rate', `$${(load.rate || 0).toLocaleString()}`, 'w-20')}
                          </span>
                        )}
                        {col.id === 'weight' && (load.weight ? `${load.weight} lbs` : '-')}
                        {col.id === 'commodity' && renderEditableCell(load, 'commodity', load.commodity, 'w-28')}
                        {col.id === 'remarks' && renderEditableCell(load, 'remarks', load.remarks, 'w-36')}
                      </TableCell>
                    ))}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                          <DropdownMenuItem onClick={() => handleEditLoad(load)} className="text-white">
                            <Edit2 className="h-4 w-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteLoad(load.id)} className="text-red-400">
                            <Trash2 className="h-4 w-4 mr-2" />Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Add Sheet */}
      <AddLocalLoad
        open={addSheetOpen}
        onOpenChange={setAddSheetOpen}
        masterData={masterData}
        customers={customers}
        onSuccess={() => {
          fetchLoads();
          setAddSheetOpen(false);
        }}
      />

      {/* Edit Sheet */}
      {selectedLoad && (
        <EditLocalLoad
          open={editSheetOpen}
          onOpenChange={(open) => {
            setEditSheetOpen(open);
            // Refresh dashboard data so any inline saves made INSIDE the sheet
            // reflect immediately on the table.
            if (!open) fetchLoads();
          }}
          load={selectedLoad}
          masterData={masterData}
          customers={customers}
          onSuccess={() => {
            fetchLoads();
            setEditSheetOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default LocalDashboard;
